/**
 * Chatbot Service
 * API service for chatbot conversation operations.
 */

import { api } from '@services/api';
import { CHATBOT_ENDPOINTS } from '@services/api/endpoints';
import type {
  ApiSuccessResponse,
  ConversationSummary,
  ConversationDetail,
  ChatMessage,
  StarterQuestion as StarterQuestionAPI,
  CreateConversationRequest,
} from '../types';

/**
 * Chatbot conversation API.
 */
export const chatbotApi = {
  /**
   * Create a new conversation.
   */
  createConversation: async (title?: string): Promise<ConversationDetail> => {
    const payload: CreateConversationRequest = {};
    if (title) {
      payload.title = title;
    }
    const response = await api.post<ApiSuccessResponse<ConversationDetail>>(
      CHATBOT_ENDPOINTS.CONVERSATIONS,
      payload
    );
    return response.data;
  },

  /**
   * List user's conversations.
   */
  getConversations: async (): Promise<ConversationSummary[]> => {
    const response = await api.get<ApiSuccessResponse<ConversationSummary[]>>(
      CHATBOT_ENDPOINTS.CONVERSATIONS
    );
    return response.data;
  },

  /**
   * Get a single conversation with messages.
   */
  getConversation: async (id: string | number): Promise<ConversationDetail> => {
    const response = await api.get<ApiSuccessResponse<ConversationDetail>>(
      CHATBOT_ENDPOINTS.CONVERSATION_DETAIL(id)
    );
    return response.data;
  },

  /**
   * Send a message in a conversation and get AI response.
   */
  sendMessage: async (conversationId: string | number, message: string): Promise<ChatMessage> => {
    const response = await api.post<ApiSuccessResponse<ChatMessage>>(
      CHATBOT_ENDPOINTS.SEND_MESSAGE(conversationId),
      { message }
    );
    return response.data;
  },

  /**
   * Get active starter questions (for all authenticated users).
   */
  getStarterQuestions: async (): Promise<StarterQuestionAPI[]> => {
    const response = await api.get<ApiSuccessResponse<StarterQuestionAPI[]>>(
      CHATBOT_ENDPOINTS.STARTER_QUESTIONS
    );
    return response.data;
  },
};
